/**
 * Editor de mazos.
 *
 * Dos listas y un gesto: tocar una carta de arriba la saca del mazo, tocar una
 * de abajo la mete. Sin arrastrar, sin menús contextuales, sin modo de edición.
 * En un móvil, cualquier cosa más elaborada que "tocar para mover" se convierte
 * en un tutorial.
 *
 * Los cambios no se guardan solos. Un mazo a medio editar es ilegal por
 * definición —le faltan cartas mientras lo recompones— y guardar en cada toque
 * significaría o rechazar la mitad de los toques o dejar mazos inválidos en la
 * base. Se edita en local y se confirma al final.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CardView } from '@/components/cards/CardView';
import { Button } from '@/components/ui/Button';
import { getCardMaster } from '@/data';
import { useSaveDeck, validateDeck } from '@/hooks/useDeckActions';
import { DECK_SIZE } from '@/types/game';
import type { CollectionCard, Deck } from '@/types/game';

interface DeckEditorProps {
  deck: Deck | null;
  inventory: readonly CollectionCard[];
  userId: string | undefined;
}

/** Ordena por coste y luego por nombre: es como se lee una curva de mazo. */
function byCurve(a: CollectionCard, b: CollectionCard): number {
  const left = getCardMaster(a.masterId);
  const right = getCardMaster(b.masterId);
  return left.cost - right.cost || left.name.localeCompare(right.name, 'es');
}

function CardButton({
  card,
  onClick,
  label,
}: {
  card: CollectionCard;
  onClick: () => void;
  label: string;
}) {
  return (
    <motion.button
      type="button"
      layout
      onClick={onClick}
      title={label}
      aria-label={label}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.85 }}
      transition={{ duration: 0.15 }}
      className="relative w-full"
    >
      <CardView
        card={{
          instanceId: card.id,
          masterId: card.masterId,
          level: card.level,
          owner: 'PLAYER',
        }}
        variant="board"
      />
      {card.serialNumber !== null ? (
        <span className="absolute -bottom-1 left-0 rounded-sm bg-slate-950/90 px-1 font-mono text-[9px] text-amber-200">
          #{card.serialNumber}
        </span>
      ) : null}
    </motion.button>
  );
}

export function DeckEditor({ deck, inventory, userId }: DeckEditorProps) {
  const save = useSaveDeck(userId);
  const [draft, setDraft] = useState<string[]>(deck?.cardIds ?? []);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Si el mazo llega o cambia desde el servidor (otra pestaña, una fusión que
  // repuso cartas), se recoge. Solo cuando no hay edición a medias: pisar el
  // trabajo del jugador sería peor que mostrar un dato algo viejo.
  useEffect(() => {
    setDraft((current) => (current.length === 0 && deck ? deck.cardIds : current));
  }, [deck]);

  const byId = useMemo(() => new Map(inventory.map((card) => [card.id, card])), [inventory]);

  const inDeck = useMemo(
    () => draft.map((id) => byId.get(id)).filter((card): card is CollectionCard => Boolean(card)),
    [draft, byId],
  );
  const available = useMemo(
    () => inventory.filter((card) => !draft.includes(card.id)).sort(byCurve),
    [inventory, draft],
  );

  const problem = validateDeck(draft);
  const dirty = JSON.stringify(draft) !== JSON.stringify(deck?.cardIds ?? []);

  const move = (cardId: string, into: boolean) => {
    setSaved(false);
    setError(null);
    setDraft((current) =>
      into
        ? current.length >= DECK_SIZE
          ? current
          : [...current, cardId]
        : current.filter((id) => id !== cardId),
    );
  };

  const handleSave = () => {
    if (!deck) return;
    setError(null);
    save.mutate(
      { deckId: deck.id, cardIds: draft },
      {
        onSuccess: () => setSaved(true),
        onError: (mutationError) => setError(mutationError.message),
      },
    );
  };

  if (!deck) {
    return (
      <p className="py-10 text-center text-sm text-slate-500">
        No tienes ningún mazo activo. Se crea solo al abrir la cuenta.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barra de estado: lo único que el jugador necesita saber en todo momento
          es cuántas lleva y si puede guardar. */}
      <div className="sticky top-0 z-10 -mx-2 flex items-center justify-between gap-2 bg-slate-950/90 px-2 py-2 backdrop-blur-sm sm:-mx-4 sm:px-4">
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-bold tabular-nums ${
              problem ? 'text-amber-300' : 'text-emerald-300'
            }`}
          >
            {draft.length}/{DECK_SIZE}
          </span>
          {problem ? <span className="text-[11px] text-slate-400">{problem}</span> : null}
        </div>

        <div className="flex items-center gap-2">
          {dirty ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft(deck.cardIds);
                setError(null);
                setSaved(false);
              }}
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Deshacer
            </Button>
          ) : null}
          <Button size="sm" disabled={Boolean(problem) || !dirty || save.isPending} onClick={handleSave}>
            {save.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : saved && !dirty ? (
              <Check className="h-3 w-3" aria-hidden />
            ) : null}
            {saved && !dirty ? 'Guardado' : 'Guardar mazo'}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {error ? (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            role="alert"
            className="flex items-center gap-1.5 rounded-lg bg-rose-950/70 px-3 py-2 text-xs text-rose-200 ring-1 ring-rose-500/40"
          >
            <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden />
            {error}
          </motion.p>
        ) : null}
      </AnimatePresence>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">
          En el mazo · toca para sacar
        </h3>
        {inDeck.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-600">Mazo vacío</p>
        ) : (
          <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-8 lg:grid-cols-10">
            <AnimatePresence mode="popLayout">
              {inDeck.map((card) => (
                <CardButton
                  key={card.id}
                  card={card}
                  onClick={() => move(card.id, false)}
                  label={`Sacar ${getCardMaster(card.masterId).name} del mazo`}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">
          Sueltas · toca para meter ({available.length})
        </h3>
        {available.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-600">
            Todas tus cartas están en el mazo.
          </p>
        ) : (
          <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-8 lg:grid-cols-10">
            <AnimatePresence mode="popLayout">
              {available.map((card) => (
                <CardButton
                  key={card.id}
                  card={card}
                  onClick={() => move(card.id, true)}
                  label={`Meter ${getCardMaster(card.masterId).name} en el mazo`}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>
    </div>
  );
}
